'use client';

import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { CountryService, COUNTRIES, Country } from '@/lib/country-service';
import { ChevronDown } from 'lucide-react';

export default function CountrySelector() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Country | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountrySelect = (country: Country) => {
    setSelected(country);
    setOpen(false);
    const normalizedCode = country.code.toUpperCase();
    CountryService.setSelectedCountry(normalizedCode);
    window.location.href = `/${normalizedCode.toLowerCase()}/home`;
  };

  const countries = Object.values(COUNTRIES);

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-8">
          <Image
            src="/Solynta Academy Logo.png"
            alt="Solynta Academy"
            width={64}
            height={64}
            className="w-16 h-16 mx-auto mb-6"
          />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
            Solynta Academy
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Select your region to continue
          </p>
        </div>

        <div ref={dropdownRef} className="relative w-full max-w-xs mx-auto">
          <button
            onClick={() => setOpen(!open)}
            className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            {selected ? (
              <span className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                <span className="text-lg">{selected.flag}</span>
                <span className="text-sm font-medium">{selected.name}</span>
              </span>
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500">Choose a country</span>
            )}
            <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <ul className="absolute z-10 mt-1 w-full bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg overflow-hidden">
              {countries.map((country) => (
                <li key={country.code}>
                  <button
                    onClick={() => handleCountrySelect(country)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
                  >
                    <span className="text-lg">{country.flag}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{country.name}</span>
                    <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{country.currency}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
          This helps us show the right curriculum and pricing for your region.
        </p>
      </div>
    </div>
  );
}
